import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import Sidebar from '../components/Sidebar';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Loader2,
  CreditCard,
  Package,
  Gift,
  Crown,
  Copy,
  Check,
  AlertCircle,
  TrendingUp,
  ArrowRight,
  Infinity,
  Images,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLORS = {
  S: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  M: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
  L: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
};

const TIER_LABELS = {
  S: { pl: 'do 50 zdjec', en: 'up to 50 photos' },
  M: { pl: 'do 300 zdjec', en: 'up to 300 photos' },
  L: { pl: 'bez limitu', en: 'unlimited photos' },
};

const TX_TYPE_LABELS = {
  purchase: { pl: 'Zakup', en: 'Purchase' },
  consumption: { pl: 'Uzycie', en: 'Usage' },
  refund: { pl: 'Zwrot', en: 'Refund' },
  bonus: { pl: 'Bonus', en: 'Bonus' },
  referral: { pl: 'Polecenie', en: 'Referral' },
  annual_purchase: { pl: 'Plan roczny', en: 'Annual plan' },
  upgrade: { pl: 'Upgrade', en: 'Upgrade' },
};

export default function BillingPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [packs, setPacks] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(0);
  const [referralCode, setReferralCode] = useState('');
  const [referralLoading, setReferralLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const t = useCallback((pl, en) => language === 'pl' ? pl : en, [language]);
  const TX_PER_PAGE = 10;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const purchased = searchParams.get('purchased');
    if (purchased) {
      toast.success(t('Pakiet zakupiony pomyslnie!', 'Pack purchased successfully!'));
      fetchData();
    }
  }, [searchParams]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, packsRes, txRes] = await Promise.all([
        axios.get(`${API}/billing/dashboard`),
        axios.get(`${API}/billing/packs`),
        axios.get(`${API}/billing/transactions?limit=${TX_PER_PAGE}&skip=0`),
      ]);
      setDashboard(dashRes.data);
      setPacks(packsRes.data.packs || packsRes.data);
      setTransactions(txRes.data.transactions || []);
      setTxTotal(txRes.data.total || 0);
    } catch (error) {
      toast.error(t('Blad ladowania danych', 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (page) => {
    try {
      const res = await axios.get(`${API}/billing/transactions?limit=${TX_PER_PAGE}&skip=${page * TX_PER_PAGE}`);
      setTransactions(res.data.transactions || []);
      setTxTotal(res.data.total || 0);
      setTxPage(page);
    } catch { /* ignore */ }
  };

  const handlePurchase = async (packId) => {
    setPurchasing(packId);
    try {
      const res = await axios.post(`${API}/billing/purchase`, {
        pack_id: packId,
        origin_url: window.location.href,
      });
      if (res.data.checkout_url) {
        if (res.data.mock) {
          toast.success(t('Kredyty dodane (tryb testowy)', 'Credits added (test mode)'));
          fetchData();
        } else {
          window.location.href = res.data.checkout_url;
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Blad zakupu', 'Purchase error'));
    } finally {
      setPurchasing(null);
    }
  };

  const handleReferral = async () => {
    if (!referralCode.trim()) return;
    setReferralLoading(true);
    try {
      await axios.post(`${API}/billing/referral`, { referral_code: referralCode.trim().toUpperCase() });
      toast.success(t('Kod polecajacego zastosowany!', 'Referral code applied!'));
      setReferralCode('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Nieprawidlowy kod', 'Invalid code'));
    } finally {
      setReferralLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (dashboard?.account?.referral_code) {
      navigator.clipboard.writeText(dashboard.account.referral_code);
      setCopied(true);
      toast.success(t('Skopiowano!', 'Copied!'));
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const account = dashboard?.account;
  const tiers = dashboard?.tiers || [];
  const totalCredits = (account?.credits_s || 0) + (account?.credits_m || 0) + (account?.credits_l || 0);
  const isUnlimited = account?.annual_unlimited;

  // Group packs by tier
  const packsByTier = {};
  packs.forEach(p => {
    if (!packsByTier[p.tier]) packsByTier[p.tier] = [];
    packsByTier[p.tier].push(p);
  });

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/billing" />

      <main className="lg:ml-64 p-6 lg:p-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-3xl font-bold mb-1">
              {t('Rozliczenia', 'Billing')}
            </h1>
            <p className="text-muted-foreground">
              {t('Zarzadzaj kredytami i pakietami', 'Manage credits and packs')}
            </p>
          </div>
        </div>

        {/* Credits Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {isUnlimited ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="col-span-full"
            >
              <Card className="bg-gradient-to-r from-amber-500/10 to-amber-600/5 border-amber-500/30">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-500/20 rounded-sm flex items-center justify-center">
                    <Crown className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-lg font-bold">{t('Plan Roczny Unlimited', 'Annual Unlimited Plan')}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('Bez limitu galerii i zdjec', 'Unlimited galleries and photos')}
                      {account?.annual_unlimited_expires_at && (
                        <> — {t('wazny do', 'valid until')} {new Date(account.annual_unlimited_expires_at).toLocaleDateString()}</>
                      )}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <>
              {['S', 'M', 'L'].map((tier, idx) => {
                const credits = account?.[`credits_${tier.toLowerCase()}`] || 0;
                const tierInfo = tiers.find(t => t.tier === tier);
                return (
                  <motion.div
                    key={tier}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: idx * 0.1 }}
                  >
                    <Card className="bg-card border-border hover:border-primary/30 transition-colors">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-3">
                          <Badge variant="outline" className={TIER_COLORS[tier]}>
                            {t('Galeria', 'Gallery')} {tier}
                          </Badge>
                          <Images className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <p className="text-3xl font-bold mb-1">{credits}</p>
                        <p className="text-sm text-muted-foreground">
                          {TIER_LABELS[tier][language]}
                        </p>
                        {tierInfo && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {tierInfo.price_pln} PLN / {t('galeria', 'gallery')}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
              >
                <Card className="bg-card border-border hover:border-primary/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <Badge variant="outline">{t('Razem', 'Total')}</Badge>
                      <TrendingUp className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <p className="text-3xl font-bold mb-1">{totalCredits}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('kredytow lacznie', 'total credits')}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('Wydano', 'Spent')}: {account?.total_spent_pln?.toFixed(2) || '0.00'} PLN
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </>
          )}
        </div>

        {/* Gallery Tiers Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mb-10"
        >
          <h2 className="text-xl font-semibold mb-4">{t('Rozmiary galerii', 'Gallery sizes')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {tiers.map((tier) => (
              <Card key={tier.tier} className="bg-card border-border">
                <CardContent className="p-6 text-center">
                  <Badge variant="outline" className={`${TIER_COLORS[tier.tier]} mb-3`}>
                    {tier.tier}
                  </Badge>
                  <p className="text-2xl font-bold mb-1">{tier.price_pln} PLN</p>
                  <p className="text-sm text-muted-foreground">
                    {tier.max_photos === 0
                      ? t('Bez limitu zdjec', 'Unlimited photos')
                      : `${t('do', 'up to')} ${tier.max_photos} ${t('zdjec', 'photos')}`
                    }
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {t('Pelna funkcjonalnosc', 'Full functionality')}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.div>

        {/* Credit Packs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mb-10"
        >
          <h2 className="text-xl font-semibold mb-4">{t('Pakiety kredytow', 'Credit packs')}</h2>

          {['S', 'M', 'L'].map(tier => (
            packsByTier[tier]?.length > 0 && (
              <div key={tier} className="mb-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wider">
                  {t('Galerie', 'Galleries')} {tier}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {packsByTier[tier].map(pack => (
                    <Card
                      key={pack.id}
                      className={`bg-card border-border relative ${pack.popular ? 'border-primary ring-1 ring-primary/20' : ''}`}
                    >
                      {pack.popular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <Badge className="bg-primary text-primary-foreground">
                            {t('Popularny', 'Popular')}
                          </Badge>
                        </div>
                      )}
                      <CardContent className="p-5">
                        <p className="font-semibold mb-1">
                          {language === 'pl' ? pack.name : pack.name_en}
                        </p>
                        <p className="text-2xl font-bold mb-1">{pack.price_pln} PLN</p>
                        <div className="flex items-center gap-2 mb-3">
                          <p className="text-sm text-muted-foreground">
                            {pack.price_per_gallery_pln?.toFixed(2)} PLN / {t('galeria', 'gallery')}
                          </p>
                          {pack.discount_percent > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              -{pack.discount_percent}%
                            </Badge>
                          )}
                        </div>
                        <Button
                          className="w-full"
                          size="sm"
                          disabled={purchasing === pack.id}
                          onClick={() => handlePurchase(pack.id)}
                        >
                          {purchasing === pack.id ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <CreditCard className="w-4 h-4 mr-2" />
                          )}
                          {t('Kup', 'Buy')}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )
          ))}

          {/* Special packs: wedding, unlimited */}
          {(packsByTier['mixed'] || packsByTier['unlimited']) && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wider">
                {t('Pakiety specjalne', 'Special packs')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...(packsByTier['mixed'] || []), ...(packsByTier['unlimited'] || [])].map(pack => (
                  <Card
                    key={pack.id}
                    className={`bg-card border-border ${pack.tier === 'unlimited' ? 'bg-gradient-to-r from-amber-500/5 to-amber-600/5 border-amber-500/30' : ''}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        {pack.tier === 'unlimited' ? (
                          <Crown className="w-6 h-6 text-amber-500" />
                        ) : (
                          <Package className="w-6 h-6 text-primary" />
                        )}
                        <div>
                          <p className="font-semibold">
                            {language === 'pl' ? pack.name : pack.name_en}
                          </p>
                          {pack.discount_percent > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              -{pack.discount_percent}%
                            </Badge>
                          )}
                        </div>
                      </div>
                      {pack.tier === 'unlimited' ? (
                        <p className="text-sm text-muted-foreground mb-3">
                          {t('Bez limitu galerii i zdjec przez 12 miesiecy', 'Unlimited galleries and photos for 12 months')}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground mb-3">
                          {pack.credits_s > 0 && `${pack.credits_s}×S `}
                          {pack.credits_m > 0 && `${pack.credits_m}×M `}
                          {pack.credits_l > 0 && `${pack.credits_l}×L`}
                        </p>
                      )}
                      <div className="flex items-center justify-between">
                        <p className="text-2xl font-bold">{pack.price_pln} PLN</p>
                        <Button
                          disabled={purchasing === pack.id}
                          onClick={() => handlePurchase(pack.id)}
                        >
                          {purchasing === pack.id ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <CreditCard className="w-4 h-4 mr-2" />
                          )}
                          {t('Kup', 'Buy')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        {/* Referral Program */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mb-10"
        >
          <h2 className="text-xl font-semibold mb-4">{t('Program polecen', 'Referral program')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Your code */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Gift className="w-5 h-5" />
                  {t('Twoj kod polecajacego', 'Your referral code')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  {t(
                    'Udostepnij kod znajomym fotografom. Za kazde polecenie otrzymasz 10 galerii S gratis!',
                    'Share with fellow photographers. For each referral you get 10 free S galleries!'
                  )}
                </p>
                <div className="flex gap-2">
                  <div className="flex-1 bg-secondary rounded-sm px-4 py-3 font-mono text-lg font-bold tracking-wider text-center">
                    {account?.referral_code || '------'}
                  </div>
                  <Button variant="outline" onClick={copyReferralCode}>
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                {account?.referral_bonus_claimed > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {t('Polecono', 'Referred')}: {account.referral_bonus_claimed} {t('osob', 'people')}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Apply code */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ArrowRight className="w-5 h-5" />
                  {t('Uzyj kodu polecajacego', 'Use referral code')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  {t(
                    'Wpisz kod od innego fotografa, aby dac mu bonus.',
                    'Enter a code from another photographer to give them a bonus.'
                  )}
                </p>
                {account?.referred_by ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 text-green-500" />
                    {t('Kod juz uzyty', 'Code already used')}: {account.referred_by}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      placeholder={t('Wpisz kod...', 'Enter code...')}
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                      className="font-mono tracking-wider uppercase"
                      maxLength={6}
                    />
                    <Button
                      onClick={handleReferral}
                      disabled={referralLoading || !referralCode.trim()}
                    >
                      {referralLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        t('Zastosuj', 'Apply')
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </motion.div>

        {/* Transaction History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
        >
          <h2 className="text-xl font-semibold mb-4">{t('Historia transakcji', 'Transaction history')}</h2>
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              {transactions.length === 0 ? (
                <div className="p-12 text-center">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">
                    {t('Brak transakcji', 'No transactions')}
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground text-sm">
                            {t('Data', 'Date')}
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground text-sm">
                            {t('Typ', 'Type')}
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground text-sm">
                            {t('Opis', 'Description')}
                          </th>
                          <th className="text-right py-3 px-4 font-medium text-muted-foreground text-sm">
                            {t('Kredyty', 'Credits')}
                          </th>
                          <th className="text-right py-3 px-4 font-medium text-muted-foreground text-sm">
                            {t('Kwota', 'Amount')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((tx) => {
                          const deltas = [];
                          if (tx.credits_s_delta) deltas.push(`${tx.credits_s_delta > 0 ? '+' : ''}${tx.credits_s_delta}×S`);
                          if (tx.credits_m_delta) deltas.push(`${tx.credits_m_delta > 0 ? '+' : ''}${tx.credits_m_delta}×M`);
                          if (tx.credits_l_delta) deltas.push(`${tx.credits_l_delta > 0 ? '+' : ''}${tx.credits_l_delta}×L`);
                          const typeLabel = TX_TYPE_LABELS[tx.type]?.[language] || tx.type;

                          return (
                            <tr key={tx.id} className="border-b border-border/50 hover:bg-secondary/30">
                              <td className="py-3 px-4 text-sm">
                                {new Date(tx.created_at).toLocaleDateString()}
                              </td>
                              <td className="py-3 px-4">
                                <Badge variant="secondary" className="text-xs">
                                  {typeLabel}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-sm">{tx.description}</td>
                              <td className="py-3 px-4 text-sm text-right font-mono">
                                {deltas.length > 0 ? deltas.join(' ') : '—'}
                              </td>
                              <td className="py-3 px-4 text-sm text-right font-mono">
                                {tx.amount_pln > 0 ? `${tx.amount_pln.toFixed(2)} PLN` : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {txTotal > TX_PER_PAGE && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                      <p className="text-sm text-muted-foreground">
                        {txPage * TX_PER_PAGE + 1}–{Math.min((txPage + 1) * TX_PER_PAGE, txTotal)} {t('z', 'of')} {txTotal}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={txPage === 0}
                          onClick={() => fetchTransactions(txPage - 1)}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={(txPage + 1) * TX_PER_PAGE >= txTotal}
                          onClick={() => fetchTransactions(txPage + 1)}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
